import React, { useState, useEffect } from 'react';
import { Table, Tag, Spin, App, Dropdown, Button } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import AddIcon from '@mui/icons-material/Add';
import HeaderComponent from '../components/Components/Headers/HeaderComponent';
import { PrimaryButton } from '../components/Components/Buttons/Buttons';
import BulkOrderModal from '../components/ARSCatalog/BulkOrderModal';
import OrderDetailsModal from '../components/ARSCatalog/Modals/OrderDetailsModal';
import UploadQuoteModal from '../components/ARSCatalog/Modals/UploadQuoteModal';
import QuoteResponseModal from '../components/ARSCatalog/Modals/QuoteResponseModal';
import { getCatalogOrdersByProfile, getCatalogOrdersByRole, getUserProfiles, acceptQuote, rejectQuote, callPopulateVariantFromSkuApi } from '../services/api/catalogOrdersApi';
import { secureGetItem as getSecureItem } from '../data/utils';

export default function OrderPage() {
    const { message } = App.useApp();

    // States for managing orders
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profileEmail, setProfileEmail] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [userProfiles, setUserProfiles] = useState({});
    const [currentUserRole, setCurrentUserRole] = useState(null);

    // States for bulk order modal
    const [bulkOrderModalVisible, setBulkOrderModalVisible] = useState(false);
    const [bulkOrderInitialData, setBulkOrderInitialData] = useState(null);
    const [bulkOrderInitialStep, setBulkOrderInitialStep] = useState(0);
    const [manualModalOpen, setManualModalOpen] = useState(false);
    const [resetBulkOrderStepper, setResetBulkOrderStepper] = useState(false);

    // States for order details modal
    const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // States for upload quote modal
    const [uploadQuoteModalVisible, setUploadQuoteModalVisible] = useState(false);
    const [selectedOrderForQuote, setSelectedOrderForQuote] = useState(null);

    // States for quote response modal
    const [quoteResponseModalVisible, setQuoteResponseModalVisible] = useState(false);
    const [selectedOrderForQuoteResponse, setSelectedOrderForQuoteResponse] = useState(null);

    // States for pagination debugging
    const [paginationState, setPaginationState] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // Load profile email, clientId, and user profiles from secure storage
    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Get profile email
                const storedEmail = await getSecureItem('email');
                if (storedEmail && storedEmail !== 'null' && storedEmail !== '') {
                    setProfileEmail(storedEmail);
                } else {
                    setProfileEmail(null);
                }

                // Get current user role
                const storedRole = await getSecureItem('role');
                if (storedRole && storedRole !== 'null' && storedRole !== '') {
                    setCurrentUserRole(storedRole);
                } else {
                    setCurrentUserRole(null);
                }

                // Get clientId for the BulkOrderModal
                let storedClientId = await getSecureItem('clientId');

                // If no clientId, try using ref_cliente as fallback
                if (!storedClientId || storedClientId === 'null' || storedClientId === '') {
                    const refCliente = await getSecureItem('ref_cliente');

                    if (refCliente && refCliente !== 'null' && refCliente !== '') {
                        storedClientId = refCliente;
                    }
                }

                if (storedClientId && storedClientId !== 'null' && storedClientId !== '') {
                    setClientId(storedClientId);
                } else {
                    setClientId(null);
                }

                // Load user profiles for displaying names
                const profiles = await getUserProfiles();
                setUserProfiles(profiles);
            } catch (error) {
                setProfileEmail(null);
                setClientId(null);
                setUserProfiles({});
            }
        };

        loadUserData();
    }, []);

    // Check for bulk order data from sessionStorage (from BulkAddModal)
    useEffect(() => {
        const checkBulkOrderData = () => {

            const shouldOpenModal = sessionStorage.getItem('openBulkOrderModal');
            const bulkOrderData = sessionStorage.getItem('bulkOrderData');

            if (shouldOpenModal === 'true' && bulkOrderData) {
                try {
                    const parsedData = JSON.parse(bulkOrderData);

                    setBulkOrderInitialData(parsedData);
                    setBulkOrderInitialStep(1); // Start at step 2 (index 1)
                    setBulkOrderModalVisible(true);

                    // Clear sessionStorage
                    sessionStorage.removeItem('openBulkOrderModal');
                    sessionStorage.removeItem('bulkOrderData');
                } catch (error) {
                    // Clear invalid data
                    sessionStorage.removeItem('openBulkOrderModal');
                    sessionStorage.removeItem('bulkOrderData');
                }
            }
        };

        // Check when component mounts and when clientId is available
        // Only check if the modal was not opened manually
        if (clientId && !manualModalOpen) {
            checkBulkOrderData();
        }
    }, [clientId, manualModalOpen]);

    // Load orders when profile email, role, or clientId are available
    useEffect(() => {
        loadOrders();
    }, [profileEmail, currentUserRole, clientId, message]);

    // Debug effect to monitor orders data for pagination issues
    useEffect(() => {
        if (orders.length > 0) {
            // Check for duplicate keys
            const keys = orders.map(order => order.key || order.id);
            const uniqueKeys = new Set(keys);
            if (keys.length !== uniqueKeys.size) {
            }
        }

        // Update pagination state when orders change
        setPaginationState(prev => ({
            ...prev,
            total: orders.length,
            // Reset to first page if current page would be empty
            current: Math.min(prev.current, Math.max(1, Math.ceil(orders.length / prev.pageSize)))
        }));
    }, [orders]);

    // Function to format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';

        let date;
        if (timestamp.__time__) {
            // Format with __time__
            date = new Date(timestamp.__time__);
        } else if (timestamp.seconds) {
            // Firestore Timestamp
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp instanceof Date) {
            // Already a Date object
            date = timestamp;
        } else {
            // String or other format
            date = new Date(timestamp);
        }

        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Function to get user full name from email
    const getUserFullName = (email) => {
        if (!email || !userProfiles[email]) {
            return email || 'Unknown';
        }

        const profile = userProfiles[email];
        const fullName = `${profile.firstName} ${profile.lastName}`.trim();
        return fullName || email;
    };

    // Function to get status tag colour
    const getStatusColour = (status) => {
        switch (status) {
            case 'pending_analysis':
                return 'processing';
            case 'analysis_complete':
                return 'success';
            case 'processing_addition':
                return 'warning';
            case 'quote_requested':
                return 'purple';
            case 'quote_sent':
                return 'cyan';
            case 'quote_accepted':
                return 'green';
            case 'quote_rejected':
                return 'red';
            case 'completed':
                return 'green';
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    // Function to get status display text
    const getStatusText = (status) => {
        switch (status) {
            case 'pending_analysis':
                return 'Pending Analysis';
            case 'analysis_complete':
                return 'Analysis Complete';
            case 'processing_addition':
                return 'Processing Addition';
            case 'quote_requested':
                return 'Quote Requested';
            case 'quote_sent':
                return 'Quote Sent';
            case 'quote_accepted':
                return 'Quote Accepted';
            case 'quote_rejected':
                return 'Quote Rejected';
            case 'completed':
                return 'Completed';
            case 'error':
                return 'Error';
            default:
                return status;
        }
    };

    // Function to get type display text
    const getTypeText = (type) => {
        switch (type) {
            case 'add_assets_to_catalogue':
                return 'Add Assets';
            case 'order_3d_asset':
                return 'Order Assets';
            default:
                return type;
        }
    };

    // Function to handle actions based on user role and order status
    const getActionMenuItems = (record) => {
        const items = [];

        // Actions available to Admin
        if (currentUserRole === 'Admin') {
            // Order details - always available
            items.push({
                key: 'view_details',
                label: 'Order Details',
            });

            // Upload quote - available for order_3d_asset orders in quote_requested status
            if (record.type === 'order_3d_asset' && record.status === 'quote_requested') {
                items.push({
                    key: 'upload_quote',
                    label: 'Upload Quote',
                });
            }
        }
        // Actions available to Client/Cliente
        else if (currentUserRole === 'Client' || currentUserRole === 'Cliente') {
            // Order details - always available
            items.push({
                key: 'view_details',
                label: 'Order Details',
            });

            // Respond to quote - available only if quote has been sent and order is in quote_sent status
            if (record.type === 'order_3d_asset' && record.status === 'quote_sent') {
                items.push({
                    key: 'respond_to_quote',
                    label: 'Respond to Quote',
                });
            }
        }
        // Actions for other roles (Modellista, etc.) - only order details
        else {
            items.push({
                key: 'view_details',
                label: 'Order Details',
            });
        }

        return items;
    };

    // Function to handle action clicks
    const handleActionClick = (key, record) => {
        switch (key) {
            case 'view_details':
                setSelectedOrder(record);
                setOrderDetailsModalVisible(true);
                break;

            case 'upload_quote':
                setSelectedOrderForQuote(record);
                setUploadQuoteModalVisible(true);
                break;

            case 'respond_to_quote':
                setSelectedOrderForQuoteResponse(record);
                setQuoteResponseModalVisible(true);
                break;

            default:
                message.warning(`Unknown action: ${key}`);
        }
    };

    // Table columns definition
    const columns = [
        {
            title: 'Date/Time',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: '20%',
            render: (timestamp) => formatTimestamp(timestamp),
            sorter: (a, b) => {
                // Sort by timestamp
                const aTime = a.timestamp?.seconds || 0;
                const bTime = b.timestamp?.seconds || 0;
                return bTime - aTime; // Newest first
            },
        },
        {
            title: 'Requested by',
            dataIndex: 'profile',
            key: 'profile',
            width: '25%',
            render: (email) => (
                <div>
                    <div style={{ fontWeight: '500' }}>
                        {getUserFullName(email)}
                    </div>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: '15%',
            render: (type) => (
                <Tag color={type === 'order_3d_asset' ? 'purple' : 'blue'}>
                    {getTypeText(type)}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '15%',
            render: (status) => (
                <Tag color={getStatusColour(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            align: 'center',
            render: (_, record) => {
                const menuItems = getActionMenuItems(record);

                if (menuItems.length === 0) {
                    return <span style={{ color: '#999' }}>No actions</span>;
                }

                return (
                    <Dropdown
                        menu={{
                            items: menuItems,
                            onClick: ({ key }) => handleActionClick(key, record),
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                            style={{
                                border: 'none',
                                boxShadow: 'none',
                            }}
                        />
                    </Dropdown>
                );
            },
        },
    ];

    // Funzione per determinare se l'utente può ordinare 3D Assets
    const canOrderAssets = (role) => {
        // Se il ruolo non è ancora caricato, nascondi il bottone
        if (!role) return false;
        // Il Validator non può ordinare 3D Assets
        return role !== 'Validator';
    };

    // Function to open bulk order modal
    const showBulkOrderModal = () => {
        // Clear any sessionStorage data to prevent interference
        sessionStorage.removeItem('openBulkOrderModal');
        sessionStorage.removeItem('bulkOrderData');

        // Mark as manually opened
        setManualModalOpen(true);

        // Reset any previous initial data when opening directly
        setBulkOrderInitialData(null);
        setBulkOrderInitialStep(0);

        // Trigger stepper reset
        setResetBulkOrderStepper(true);

        setBulkOrderModalVisible(true);
    };

    // Function to close bulk order modal and refresh orders
    const handleBulkOrderModalCancel = () => {
        setBulkOrderModalVisible(false);
        setBulkOrderInitialData(null);
        setBulkOrderInitialStep(0);
        setManualModalOpen(false); // Reset manual open flag
        setResetBulkOrderStepper(true); // Reset stepper for next open

        // Refresh orders list after modal closes
        if (profileEmail) {
            loadOrders();
        }
    };

    // Function to close order details modal
    const handleOrderDetailsModalCancel = () => {
        setOrderDetailsModalVisible(false);
        setSelectedOrder(null);
    };

    // Function to close upload quote modal
    const handleUploadQuoteModalCancel = () => {
        setUploadQuoteModalVisible(false);
        setSelectedOrderForQuote(null);
    };

    // Function to handle successful quote upload
    const handleQuoteUploadSuccess = () => {
        // Refresh orders list after successful upload
        if (profileEmail) {
            loadOrders();
        }
    };

    // Function to close quote response modal
    const handleQuoteResponseModalCancel = () => {
        setQuoteResponseModalVisible(false);
        setSelectedOrderForQuoteResponse(null);
    };

    // Function to handle quote response success
    const handleQuoteResponseSuccess = async (response, reason = null) => {
        try {
            if (response === 'accepted') {
                await acceptQuote(selectedOrderForQuoteResponse.id);
                message.success('Quote accepted successfully!');

                // Call the POPULATE_VARIANT_FROM_SKU API in background after confirmation
                (async () => {
                    try {
                        await callPopulateVariantFromSkuApi(selectedOrderForQuoteResponse.id);
                    } catch (apiError) {
                        console.error('Background API call failed, but quote was accepted successfully:', apiError);
                    }
                })();

            } else if (response === 'rejected') {
                await rejectQuote(selectedOrderForQuoteResponse.id, reason);
                message.success('Quote rejected successfully!');
            }

            // Refresh orders list after response
            if (profileEmail) {
                loadOrders();
            }
        } catch (error) {
            message.error('Failed to process quote response. Please try again.');
        }
    };

    // Function to load orders (extracted for reuse)
    const loadOrders = async () => {
        if (!profileEmail) return;

        try {
            setLoading(true);

            // Use role-based order fetching if role is available, otherwise fallback to profile-based
            let fetchedOrders;
            if (currentUserRole) {
                fetchedOrders = await getCatalogOrdersByRole(profileEmail, currentUserRole, clientId);
            } else {
                fetchedOrders = await getCatalogOrdersByProfile(profileEmail);
            }

            // Filter orders based on user role
            let filteredOrders = fetchedOrders;
            if (currentUserRole === 'Client' || currentUserRole === 'Cliente') {
                // Client users should only see orders from 'quote_requested' status onwards
                const allowedStatuses = [
                    'quote_requested',
                    'quote_sent',
                    'quote_accepted',
                    'quote_rejected',
                    'completed'
                ];
                filteredOrders = fetchedOrders.filter(order =>
                    allowedStatuses.includes(order.status)
                );
            }

            // Ensure each order has a unique ID for pagination
            const ordersWithKeys = filteredOrders.map((order, index) => ({
                ...order,
                key: order.id || `order-${index}`, // Fallback key if ID is missing
            }));

            setOrders(ordersWithKeys);
        } catch (error) {
            message.error('Error loading orders');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <HeaderComponent
                title="Orders"
                rightContent={
                    canOrderAssets(currentUserRole) && (
                        <PrimaryButton icon={<AddIcon />} onClick={showBulkOrderModal}>
                            Order 3D Assets
                        </PrimaryButton>
                    )
                }
                isVisible={false}
                useSpaceBetween={true}
            />

            <div style={{ marginTop: '1rem' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '16px' }}>Loading orders...</p>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={orders}
                        rowKey={(record) => record.key || record.id}
                        pagination={{
                            current: paginationState.current,
                            pageSize: paginationState.pageSize,
                            total: orders.length,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} orders`,
                            onChange: (page, pageSize) => {
                                setPaginationState(prev => ({
                                    ...prev,
                                    current: page,
                                    pageSize: pageSize
                                }));
                            },
                            onShowSizeChange: (current, size) => {
                                setPaginationState(prev => ({
                                    ...prev,
                                    current: 1, // Reset to first page when changing page size
                                    pageSize: size
                                }));
                            }
                        }}
                        locale={{
                            emptyText: profileEmail ? 'No orders found' : 'Profile email not available'
                        }}
                        scroll={{ x: 'max-content' }}
                        defaultSortOrder="descend"
                        sortDirections={['descend', 'ascend']}
                    />
                )}
            </div>

            <BulkOrderModal
                visible={bulkOrderModalVisible}
                onCancel={handleBulkOrderModalCancel}
                clientId={clientId}
                initialData={bulkOrderInitialData}
                initialStep={bulkOrderInitialStep}
                resetStepper={resetBulkOrderStepper}
                onResetComplete={() => setResetBulkOrderStepper(false)}
            />

            <OrderDetailsModal
                visible={orderDetailsModalVisible}
                onCancel={handleOrderDetailsModalCancel}
                order={selectedOrder}
                userProfiles={userProfiles}
            />

            <UploadQuoteModal
                visible={uploadQuoteModalVisible}
                onCancel={handleUploadQuoteModalCancel}
                order={selectedOrderForQuote}
                onSuccess={handleQuoteUploadSuccess}
            />

            <QuoteResponseModal
                visible={quoteResponseModalVisible}
                onCancel={handleQuoteResponseModalCancel}
                order={selectedOrderForQuoteResponse}
                onSuccess={handleQuoteResponseSuccess}
            />
        </div>
    );
}